import React, { useState } from 'react';
import { PortfolioItem } from '@/hooks/useProfile';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Briefcase, MapPin, PenTool, Code, Camera, Type, Megaphone } from 'lucide-react';
import MobileShotsTab from './MobileShotsTab';
import MobileDescriptionTab from './MobileDescriptionTab';
import Link from 'next/link';
import { convertSupabaseUrlToRenderUrl } from '@/utils/imageUtils';

interface MobilePortfolioModalContentProps {
  item: PortfolioItem;
  onClose: () => void;
  username: string;
  username_slug: string;
  profile: {
    avatar_url?: string | null;
    profession?: string | null;
    display_name?: string | null;
    location?: string | null;
  };
}

type TabType = 'shots' | 'description';

// Helper function to match profile card's profession icon logic
const getProfessionIcon = (profession: string) => {
  const lowerProf = (profession || '').toLowerCase();
  if (
    lowerProf.includes('design') ||
    lowerProf.includes('graphic') ||
    lowerProf.includes('ui') ||
    lowerProf.includes('ux')
  ) {
    return <PenTool className="h-4 w-4 mr-1 shrink-0 text-gray-500" />;
  } else if (
    lowerProf.includes('develop') ||
    lowerProf.includes('engineer') ||
    lowerProf.includes('code') ||
    lowerProf.includes('program')
  ) {
    return <Code className="h-4 w-4 mr-1 shrink-0 text-gray-500" />;
  } else if (lowerProf.includes('photo') || lowerProf.includes('camera')) {
    return <Camera className="h-4 w-4 mr-1 shrink-0 text-gray-500" />;
  } else if (
    lowerProf.includes('write') ||
    lowerProf.includes('content') ||
    lowerProf.includes('editor')
  ) {
    return <Type className="h-4 w-4 mr-1 shrink-0 text-gray-500" />;
  } else if (
    lowerProf.includes('market') ||
    lowerProf.includes('brand') ||
    lowerProf.includes('social')
  ) {
    return <Megaphone className="h-4 w-4 mr-1 shrink-0 text-gray-500" />;
  } else {
    return <Briefcase className="h-4 w-4 mr-1 shrink-0 text-gray-500" />;
  }
};

// Helper to format location/country (capitalize each word)
const formatLocation = (location: string | null | undefined): string => {
  if (!location) return '';
  return location
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const MobilePortfolioModalContent = ({ 
  item, 
  onClose, 
  username,
  username_slug,
  profile
}: MobilePortfolioModalContentProps) => {
  const [activeTab, setActiveTab] = useState<TabType>('shots');

  const handleTabChange = (value: string) => {
    setActiveTab(value as TabType);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Modal Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          {/* Profile Picture */}
          <Link href={`/u/${username_slug}`} onClick={onClose} className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 block ring-0 focus:ring-2 focus:ring-accent transition-shadow">
            {profile.avatar_url ? (
              <img
                src={convertSupabaseUrlToRenderUrl(profile.avatar_url) || "/placeholder-image.svg"}
                alt={username}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                <span className="text-gray-600 text-sm font-medium">
                  {username.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </Link>
          
          {/* Name and Role */}
          <div className="flex flex-col">
            <Link href={`/u/${username_slug}`} onClick={onClose} className="font-semibold text-gray-900 hover:underline focus:underline outline-none">
              {profile.display_name || username}
            </Link>
            {(profile.profession || profile.location) && (
              <div className="flex items-center gap-3 mt-0.5 text-gray-500 text-sm">
                {profile.profession && (
                  <span className="flex items-center gap-1">
                    {profile.profession}
                  </span>
                )}
                {profile.location && (
                  <span className="flex items-center gap-1">
                    {formatLocation(profile.location)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        

      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="flex justify-center py-[8px]">
          <TabsList className="inline-flex items-center border border-zinc-200 rounded-full bg-white overflow-hidden h-10">
            <TabsTrigger
              value="shots"
              className="px-4 py-1.5 text-[12px] font-medium rounded-full transition-all focus:outline-none
                data-[state=active]:bg-[#E5E5E5] data-[state=active]:text-black data-[state=active]:font-semibold
                data-[state=inactive]:bg-transparent data-[state=inactive]:text-[#8A898C] data-[state=inactive]:font-medium"
            >
              Shots
            </TabsTrigger>
            <TabsTrigger
              value="description"
              className="px-4 py-1.5 text-[12px] font-medium rounded-full transition-all focus:outline-none
                data-[state=active]:bg-[#E5E5E5] data-[state=active]:text-black data-[state=active]:font-semibold
                data-[state=inactive]:bg-transparent data-[state=inactive]:text-[#8A898C] data-[state=inactive]:font-medium"
            >
              Description
            </TabsTrigger>
          </TabsList>
        </div>
      </Tabs>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden px-4">
        {activeTab === 'shots' ? (
          <MobileShotsTab item={item} />
        ) : (
          <MobileDescriptionTab item={item} />
        )}
      </div>
    </div>
  );
};

export default MobilePortfolioModalContent; 